import { useState, useEffect, useCallback } from "react";

// Network Information API - not in all TS libs
interface NetworkInformation {
  effectiveType?: string;
  saveData?: boolean;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
}

const LOW_BANDWIDTH_EFFECTIVE_TYPES = ["slow-2g", "2g", "3g", "slow-3g", "slow-4g"] as const;

/** Slow API response threshold: requests taking longer than this indicate a slow connection. */
const SLOW_REQUEST_THRESHOLD_MS = 3000;

/**
 * Synchronous check for low bandwidth. Use for API timeouts, speculative
 * preloading, etc. Returns true when navigator.connection indicates slow
 * (saveData, effectiveType slow-2g/2g/3g). Supported in Chrome, Edge, Android.
 * Safari/Firefox lack the API and return false: that is why this signal only
 * ever *reduces* work and never decides whether an original is downloaded
 * (previews are the default on every browser, see lib/photoSelection).
 */
export function isLowBandwidth(): boolean {
  const conn = (navigator as Navigator & { connection?: NetworkInformation })
    .connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  if (
    conn.effectiveType &&
    LOW_BANDWIDTH_EFFECTIVE_TYPES.includes(
      conn.effectiveType as (typeof LOW_BANDWIDTH_EFFECTIVE_TYPES)[number],
    )
  ) {
    return true;
  }
  return false;
}

/**
 * Detects low bandwidth using the Network Information API and observed request timing.
 *
 * - Uses navigator.connection (Chrome, Edge, Android) when available.
 * - Falls back to timing-based detection via `reportSlowRequest` for Safari / Firefox.
 *
 * Call `reportSlowRequest(durationMs)` after any API fetch. If it exceeds
 * SLOW_REQUEST_THRESHOLD_MS the hook flags the connection as slow for the
 * lifetime of the page, which turns off speculative preloading.
 */
export function useConnectionQuality(): {
  lowBandwidth: boolean;
  reportSlowRequest: (durationMs: number) => void;
} {
  const [lowBandwidth, setLowBandwidth] = useState(() => isLowBandwidth());

  useEffect(() => {
    const conn = (navigator as Navigator & { connection?: NetworkInformation })
      .connection;

    if (conn?.addEventListener) {
      const handleChange = () => setLowBandwidth((prev) => prev || isLowBandwidth());
      conn.addEventListener("change", handleChange);
      return () => {
        if (conn.removeEventListener) {
          conn.removeEventListener("change", handleChange);
        }
      };
    }

    return undefined;
  }, []);

  const reportSlowRequest = useCallback((durationMs: number) => {
    if (durationMs >= SLOW_REQUEST_THRESHOLD_MS) {
      setLowBandwidth(true);
    }
  }, []);

  return { lowBandwidth, reportSlowRequest };
}
